// ─── AdminUser — returned by /api/admin/me ────────────────────────────────────

export interface AdminUser {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  [key: string]: unknown;
}

// ─── Core shared ─────────────────────────────────────────────────────────────

export interface MultiLangText {
  zh: string;
  en: string;
  ja: string;
}

// ─── services ────────────────────────────────────────────────────────────────

export interface ServiceFeature {
  zh: string;
  en: string;
  ja: string;
}

export interface Service {
  id: string;
  service_code: string;
  title_zh: string;
  title_en: string;
  title_ja: string;
  short_desc_zh: string;
  short_desc_en: string;
  short_desc_ja: string;
  base_price_twd: number;
  duration_hours: number;
  city_scope: string[];
  cover_image_url: string;
  icon: string;
  features: ServiceFeature[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── service_options ─────────────────────────────────────────────────────────

export interface ServiceOption {
  id: string;
  service_id: string;
  option_code: string;
  title_zh: string;
  title_en: string;
  title_ja: string;
  price_delta_twd: number;
  is_active: boolean;
  sort_order: number;
}

// ─── portfolio_items ─────────────────────────────────────────────────────────

export type MediaType = "image" | "video";
export type HomepageDisplayMode = "reel" | "still" | "both";

export interface PortfolioItem {
  id: string;
  title: string;
  city: string;
  shoot_type: string;
  media_type: MediaType;
  thumbnail_url: string;
  media_url: string;
  cover_image_url: string;
  description_zh: string;
  description_en: string;
  description_ja: string;
  photographer_id: string;
  is_published: boolean;
  is_featured: boolean;
  is_showreel: boolean;
  homepage_display_mode: HomepageDisplayMode;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── homepage_sections ───────────────────────────────────────────────────────

export type SectionKey = "hero" | "philosophy" | "cta" | string;

export interface HomepageSection {
  id: string;
  section_key: SectionKey;
  title_zh: string;
  title_en: string;
  title_ja: string;
  content_zh: string;
  content_en: string;
  content_ja: string;
  image_url: string;
  is_active: boolean;
  sort_order: number;
}

// ─── photographers — Codex Operations Core v1 schema (flat, no JSONB) ─────────

export type PhotographerStatus = "active" | "inactive" | "on_leave" | "suspended";
export type PhotographerGender = "male" | "female" | "prefer_not_to_say";

export interface Photographer {
  id: string;
  photographer_code: string;
  display_name: string;
  legal_name: string;
  email: string;
  phone: string;
  country_code: string;
  city_base: string;
  languages: string[];
  status: PhotographerStatus;
  portfolio_url: string;
  notes: string;
  is_active: boolean;
  /** Optional: used for matching only, never exposed as a filter to all clients */
  gender?: PhotographerGender;
  created_at: string;
  updated_at: string;
}

// ─── photographer_availability ────────────────────────────────────────────────

export type AvailabilityStatus = "available" | "booked" | "blocked";

export interface PhotographerAvailability {
  id: string;
  photographer_id: string;
  available_date: string;
  available_start_time: string;
  available_end_time: string;
  city: string;
  status: AvailabilityStatus;
  note: string;
  created_at: string;
  updated_at: string;
}

// ─── orders ──────────────────────────────────────────────────────────────────

export type OrderStatus = "pending" | "confirmed" | "cancelled" | "completed";
export type PaymentStatus = "unpaid" | "paid" | "refunded" | "partial";
export type PhotographerPreference = "none" | "female_preferred";

export interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service_id: string;
  selected_city: string;
  shoot_date: string;
  start_time: string;
  end_time: string;
  number_of_reels: number;
  /** Codex P-system: canonical P unit for dispatch accumulation */
  p_units: number;
  /** Codex P-system: FK to dispatch_batches.id, null = unbatched */
  batch_id: string | null;
  subtotal_twd: number;
  addons_total_twd: number;
  total_twd: number;
  currency: string;
  order_status: OrderStatus;
  payment_status: PaymentStatus;
  special_requests: string;
  internal_notes: string;
  /** Customer's photographer style preference — used for matching, not a restriction */
  photographer_preference?: PhotographerPreference;
  created_at: string;
  updated_at: string;
}

// ─── booking_confirmations ────────────────────────────────────────────────────

export type ConfirmationStatus = "pending" | "confirmed" | "declined" | "replacement_needed";

export interface BookingConfirmation {
  id: string;
  order_id: string;
  photographer_id: string;
  confirmation_status: ConfirmationStatus;
  confirmed_at: string | null;
  declined_at: string | null;
  replacement_required: boolean;
  note: string;
  created_at: string;
  updated_at: string;
}

// ─── legal_documents ─────────────────────────────────────────────────────────

export type LegalDocType =
  | "terms"
  | "service_agreement"
  | "cancellation"
  | "privacy"
  | "photo_release"
  | "photographer_contract"
  | "platform_disclaimer"
  | "cross_border";

export const LEGAL_DOC_TYPES: LegalDocType[] = [
  "terms",
  "service_agreement",
  "cancellation",
  "privacy",
  "photo_release",
  "photographer_contract",
  "platform_disclaimer",
  "cross_border",
];

/** Language keys used for title_i18n / content_i18n JSON objects */
export const LEGAL_I18N_LANGS = ["zh-TW", "en-US", "ja-JP"] as const;
export type LegalI18nLang = typeof LEGAL_I18N_LANGS[number];

export interface LegalDocument {
  id: string;
  doc_type: LegalDocType;
  /** Unique machine key for system-enforced consent (e.g. booking checkout) */
  doc_key: string;
  title_i18n: Record<LegalI18nLang, string>;
  content_i18n: Record<LegalI18nLang, string>;
  version: string;
  effective_date: string;
  is_active: boolean;
  /** Audiences this doc targets — e.g. 'booking_customer', 'photographer' */
  audience: string[];
  /** Contexts where acceptance is required — 'booking' | 'photographer_onboarding' */
  required_for: string[];
  /** Whether this document must be accepted (true) or is optional */
  is_mandatory: boolean;
  /** Display ordering within a context */
  display_order: number;
  created_at: string;
  updated_at: string;
}

// ─── booking_cost_items ───────────────────────────────────────────────────────

export type CostType = "flight" | "train" | "accommodation" | "local_transport" | "ticket" | "other";
export type CostStatus = "estimated" | "booked" | "paid" | "non_refundable";

export interface BookingCostItem {
  id: string;
  order_id: string;
  cost_type: CostType;
  amount_twd: number;
  currency: string;
  cost_status: CostStatus;
  description: string;
  created_at: string;
  updated_at: string;
}

// ─── booking_notes ────────────────────────────────────────────────────────────

export type NoteType = "internal" | "customer_visible" | "photographer_visible";

export interface BookingNote {
  id: string;
  order_id: string;
  note_type: NoteType;
  content: string;
  created_by: string;
  created_at: string;
}

// ─── order_cancellation_logs ──────────────────────────────────────────────────

export interface OrderCancellationLog {
  id: string;
  order_id: string;
  cancelled_at: string;
  cancelled_by: string;
  reason: string;
  refund_amount_twd: number;
  penalty_amount_twd: number;
  non_refundable_cost_twd: number;
  note: string;
}

// ─── order_reschedule_logs ────────────────────────────────────────────────────

export interface OrderRescheduleLog {
  id: string;
  order_id: string;
  original_shoot_date: string;
  new_shoot_date: string;
  requested_at: string;
  approved_at: string | null;
  reason: string;
  reschedule_count: number;
  note: string;
}

// ─── itineraries ──────────────────────────────────────────────────────────────

export type ItineraryStatus = "draft" | "confirmed" | "updated" | "cancelled";

export interface Itinerary {
  id: string;
  order_id: string;
  /** FK to photographers.id — assigned photographer for this itinerary */
  photographer_id: string | null;
  meeting_point: string;
  meeting_time: string | null;
  map_url: string;
  /** shoot_notes — customer & photographer visible */
  notes: string;
  /** Admin-only notes — never shown to customer or photographer */
  internal_notes: string;
  status: ItineraryStatus;
  created_at: string;
  updated_at: string;
}

/** Enriched view: itinerary + order context for table display */
export interface ItineraryView extends Itinerary {
  order_number: string;
  selected_city: string;
  shoot_date: string;
  customer_name: string;
  photographer_name: string;
}

/** Enriched view: booking_confirmation + order + photographer for assignment table */
export type AssignmentStatus =
  | "assigned"
  | "confirmed"
  | "completed"
  | "declined"
  | "replacement_needed";

export interface AssignmentView {
  id: string; // booking_confirmation id
  order_id: string;
  order_number: string;
  photographer_id: string;
  photographer_name: string;
  selected_city: string;
  shoot_date: string;
  confirmation_status: ConfirmationStatus;
  assignment_status: AssignmentStatus;
  note: string;
  created_at: string;
  updated_at: string;
}

// ─── Admin state ─────────────────────────────────────────────────────────────

export interface CmsState {
  services: Service[];
  serviceOptions: ServiceOption[];
  portfolioItems: PortfolioItem[];
  homepageSections: HomepageSection[];
  photographers: Photographer[];
  photographerAvailability: PhotographerAvailability[];
  orders: Order[];
  bookingConfirmations: BookingConfirmation[];
  legalDocuments: LegalDocument[];
  bookingCostItems: BookingCostItem[];
  bookingNotes: BookingNote[];
  orderCancellationLogs: OrderCancellationLog[];
  orderRescheduleLogs: OrderRescheduleLog[];
  itineraries: Itinerary[];
}

// ─── relay_threads / relay_messages  (DEPRECATED — do not use) ───────────────
// kept only as dead type stubs; no code should reference these tables anymore.

// ─── Codex 005_relay_messaging_v1 — conversation_threads / conversation_messages / relay_events ──

export type ConversationParticipantType = "customer" | "photographer" | "internal";
export type ConversationThreadStatus = "open" | "closed" | "flagged";
export type ConversationSenderType = "admin" | "photographer" | "customer" | "system";
export type ConversationMessageType = "text" | "system_notice" | "itinerary_update" | "warning";
export type ConversationRiskLevel = "none" | "low" | "medium" | "high";

export interface ConversationThread {
  id: string;
  order_id: string | null;
  participant_type: ConversationParticipantType;
  subject: string;
  status: ConversationThreadStatus;
  /** Codex API-provided risk level — do NOT derive in frontend */
  risk_level: ConversationRiskLevel;
  /** Human-readable risk summary from Codex API */
  risk_summary: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  thread_id: string;
  sender_type: ConversationSenderType;
  sender_id: string;
  /** Primary message body (replaces old "content") */
  body: string;
  message_type: ConversationMessageType;
  /** Array of risk flag strings from Codex API response */
  risk_flags: string[];
  /** Structured warning metadata from Codex API (nullable) */
  warning_meta: Record<string, unknown> | null;
  /** Set by Codex API when message violates platform policy */
  is_blocked: boolean;
  is_read_admin: boolean;
  is_read_photographer: boolean;
  is_read_customer: boolean;
  created_at: string;
}

export interface RelayEvent {
  id: string;
  thread_id: string | null;
  event_type: string;
  actor_type: string;
  actor_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

/** Enriched view: thread + order context */
export interface ConversationThreadView extends ConversationThread {
  order_number?: string;
  customer_name?: string;
  shoot_date?: string;
  selected_city?: string;
  latest_message?: string;
  latest_message_at?: string;
  unread_count_admin?: number;
}

// ─── Backward-compat aliases (remove after all consumers migrated) ────────────
/** @deprecated use ConversationParticipantType */
export type RelayThreadType = ConversationParticipantType;
/** @deprecated use ConversationThread */
export type RelayThread = ConversationThread;
/** @deprecated use ConversationMessage */
export type RelayMessage = ConversationMessage;
/** @deprecated use ConversationThreadView */
export type RelayThreadView = ConversationThreadView;
/** @deprecated use ConversationSenderType */
export type RelaySenderRole = ConversationSenderType;
/** @deprecated use ConversationThreadStatus */
export type RelayThreadStatus = ConversationThreadStatus;
